import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Save, Search, Zap, X, SlidersHorizontal, AlertTriangle, CheckCircle2 } from "lucide-react";
import * as adminApi from "../../api/adminApi";
import { useToast } from "../../context/ToastContext";
import { resolveImageUrl } from "../../utils/images";
import { formatDate, formatProductSize } from "../../utils/format";
import FastStockUpdate from "./FastStockUpdate";
import "../AccountForms.css";

export default function AdminStock() {
  const { addToast } = useToast();
  const [data, setData] = useState({ items: [], page: 1, pageSize: 15, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState({});
  const [stockInputs, setStockInputs] = useState({});

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [stockFilter, setStockFilter] = useState("all"); // 'all' | 'low' | 'out' | 'changed'

  // Fast Update modal states
  const [fastUpdateModal, setFastUpdateModal] = useState(false);
  const [fastUpdateItem, setFastUpdateItem] = useState(null);

  const refresh = () => {
    setLoading(true);
    adminApi
      .fetchAdminStock({ page, pageSize: 15 })
      .then((res) => {
        setData(res);
        const inputs = {};
        res.items.forEach((item) => {
          inputs[`${item.productId}-${item.colorId}-${item.sizeId}`] = item.quantity;
        });
        setStockInputs(inputs);
      })
      .catch((err) => addToast(err.message || "Failed to load stock data", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .fetchAdminStock({ page, pageSize: 15 })
      .then((res) => {
        if (!cancelled) {
          setData(res);
          const inputs = {};
          res.items.forEach((item) => {
            inputs[`${item.productId}-${item.colorId}-${item.sizeId}`] = item.quantity;
          });
          setStockInputs(inputs);
        }
      })
      .catch((err) => {
        if (!cancelled) addToast(err.message || "Failed to load stock data", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, addToast]);

  const handleUpdate = async (item) => {
    const key = `${item.productId}-${item.colorId}-${item.sizeId}`;
    const newStockValue = Number(stockInputs[key]);

    if (isNaN(newStockValue) || newStockValue < 0) {
      addToast("Invalid stock value", "error");
      return;
    }

    setUpdating((prev) => ({ ...prev, [key]: true }));

    try {
      const updatedRow = await adminApi.setAdminStock(item.productId, {
        sizeId: item.sizeId,
        colorId: item.colorId,
        newStockValue,
      });

      setData((prev) => ({
        ...prev,
        items: prev.items.map((x) =>
          x.productId === item.productId && x.sizeId === item.sizeId && x.colorId === item.colorId
            ? updatedRow
            : x
        ),
      }));
      addToast("Stock updated successfully", "success");
    } catch (err) {
      addToast(err.message || "Failed to update stock", "error");
    } finally {
      setUpdating((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleInputChange = (item, value) => {
    const key = `${item.productId}-${item.colorId}-${item.sizeId}`;
    setStockInputs((prev) => ({ ...prev, [key]: value }));
  };

  const openFastUpdateForItem = (item) => {
    setFastUpdateItem(item);
    setFastUpdateModal(true);
  };

  const openGenericFastUpdate = () => {
    setFastUpdateItem(null);
    setFastUpdateModal(true);
  };

  // Client-side filtering of the active page items
  const filteredItems = useMemo(() => {
    return data.items.filter((item) => {
      // 1. Text search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const code = (item.code || "").toLowerCase();
        const color = (item.color || "").toLowerCase();
        const size = formatProductSize(item.size).toLowerCase();
        const id = (item.productId || "").toLowerCase();
        const matches = code.includes(q) || color.includes(q) || size.includes(q) || id.includes(q);
        if (!matches) return false;
      }

      // 2. Status filter
      const key = `${item.productId}-${item.colorId}-${item.sizeId}`;
      const currentInput = Number(stockInputs[key] ?? item.quantity);

      if (stockFilter === "low") {
        return item.quantity > 0 && item.quantity <= 5;
      }
      if (stockFilter === "out") {
        return item.quantity === 0;
      }
      if (stockFilter === "changed") {
        return currentInput !== item.quantity;
      }

      return true;
    });
  }, [data.items, searchQuery, stockFilter, stockInputs]);

  const totalPages = Math.ceil(data.totalCount / data.pageSize) || 1;

  if (loading && data.items.length === 0) {
    return (
      <div className="page-loading">
        <span className="page-loading__dot" />
      </div>
    );
  }

  return (
    <div className="admin-products">
      {/* Page Header */}
      <div className="admin-products__head">
        <div>
          <p className="section-label">Inventory Management</p>
          <h2 className="admin-products__title">Stock</h2>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button type="button" className="btn-primary" onClick={openGenericFastUpdate} style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
            <Zap size={16} /> Fast Stock Update
          </button>
          <Link to="/admin" className="btn-outline">
            ← Dashboard
          </Link>
        </div>
      </div>

      {/* Filter & Live Search Toolbar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "14px",
          background: "var(--surface-card)",
          padding: "14px 18px",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          marginBottom: "16px",
          boxShadow: "var(--shadow-soft)",
        }}
      >
        {/* Search input */}
        <div style={{ position: "relative", minWidth: "260px", flex: "1 1 300px" }}>
          <Search size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter current page by SKU, color, or size..."
            style={{
              width: "100%",
              padding: "8px 32px 8px 36px",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              fontSize: "13px",
              outline: "none",
              background: "var(--surface-input)",
              color: "var(--text-primary)",
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", padding: "4px", color: "var(--text-muted)" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Quick Filter Pills */}
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: "12px", color: "var(--text-muted)", marginRight: "4px" }}>Filter:</span>
          {[
            { id: "all", label: "All Items" },
            { id: "low", label: "Low Stock (≤ 5)" },
            { id: "out", label: "Out of Stock (0)" },
            { id: "changed", label: "Unsaved Changes" },
          ].map((tab) => {
            const isActive = stockFilter === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStockFilter(tab.id)}
                style={{
                  padding: "5px 12px",
                  fontSize: "12px",
                  borderRadius: "var(--radius)",
                  border: isActive ? "1.5px solid var(--gold)" : "1px solid var(--border)",
                  background: isActive ? "var(--gold)" : "var(--surface-input)",
                  color: isActive ? "#ffffff" : "var(--text-secondary)",
                  fontWeight: isActive ? 600 : 400,
                  transition: "all 0.15s ease",
                  cursor: "pointer",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stock Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Item</th>
              <th>SKU / Details</th>
              <th>Last Update</th>
              <th>Previous Qty</th>
              <th>Current Stock</th>
              <th style={{ width: 140, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const key = `${item.productId}-${item.colorId}-${item.sizeId}`;
              const isUpdating = updating[key];
              const currentInput = stockInputs[key] ?? item.quantity;
              const hasChanged = Number(currentInput) !== item.quantity;
              const isOutOfStock = item.quantity === 0;
              const isLowStock = item.quantity > 0 && item.quantity <= 5;

              return (
                <tr key={key}>
                  <td>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        border: "1px solid var(--border)",
                        background: item.thumbnailImage
                          ? `url(${resolveImageUrl(item.thumbnailImage)}) center/cover`
                          : "var(--cream)",
                        borderRadius: "var(--radius)",
                      }}
                    />
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <code
                          style={{
                            fontSize: "12px",
                            fontFamily: "monospace",
                            color: "var(--text-secondary)",
                            background: "var(--surface-input)",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            border: "1px solid var(--border)",
                          }}
                        >
                          {item.code || "NO SKU"}
                        </code>
                        {isOutOfStock && (
                          <span style={{ fontSize: "10px", color: "#dc2626", background: "rgba(220, 38, 38, 0.15)", padding: "2px 6px", borderRadius: "2px", fontWeight: 600 }}>
                            OUT
                          </span>
                        )}
                        {isLowStock && (
                          <span style={{ fontSize: "10px", color: "#d97706", background: "rgba(217, 119, 6, 0.15)", padding: "2px 6px", borderRadius: "2px", fontWeight: 600 }}>
                            LOW
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                        {formatProductSize(item.size)} · {item.color}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                    {formatDate(item.lastUpdate)}
                  </td>
                  <td style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                    {item.lastEnterdQuantity}
                  </td>
                  <td>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="number"
                        min="0"
                        value={currentInput}
                        onChange={(e) => handleInputChange(item, e.target.value)}
                        style={{
                          width: 70,
                          padding: "6px 8px",
                          border: hasChanged ? "1.5px solid var(--gold)" : "1px solid var(--border)",
                          borderRadius: "4px",
                          fontFamily: "var(--font-mono)",
                          textAlign: "center",
                          background: hasChanged ? "var(--surface-accent)" : "var(--surface-input)",
                          color: "var(--text-primary)",
                          fontWeight: hasChanged ? 600 : "normal",
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                      {/* Save Button */}
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={isUpdating || !hasChanged}
                        onClick={() => handleUpdate(item)}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                        title={hasChanged ? "Save updated quantity" : "No changes to save"}
                      >
                        <Save size={14} />
                        {isUpdating ? "..." : "Save"}
                      </button>

                      {/* Fast Update shortcut */}
                      <button
                        type="button"
                        className="btn-outline"
                        onClick={() => openFastUpdateForItem(item)}
                        style={{ padding: "6px 8px", fontSize: "12px" }}
                        title="Open Fast Stock Update for this product"
                      >
                        <Zap size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 && !loading && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                    <Search size={24} style={{ color: "var(--border)" }} />
                    <span>No stock records matching your criteria on this page.</span>
                    {(searchQuery || stockFilter !== "all") && (
                      <button
                        type="button"
                        className="btn-outline"
                        onClick={() => {
                          setSearchQuery("");
                          setStockFilter("all");
                        }}
                        style={{ padding: "4px 12px", fontSize: "12px", marginTop: "4px" }}
                      >
                        Reset filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 }}>
          <button
            type="button"
            className="btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span style={{ fontSize: 14, color: "var(--text-muted)" }}>
            Page {page} of {totalPages} (Total: {data.totalCount} records)
          </span>
          <button
            type="button"
            className="btn-outline"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Fast Update Modal */}
      {fastUpdateModal && (
        <FastStockUpdate
          onClose={() => {
            setFastUpdateModal(false);
            setFastUpdateItem(null);
          }}
          onStockUpdated={refresh}
          initialItem={fastUpdateItem}
          initialProductCode={fastUpdateItem?.code}
        />
      )}
    </div>
  );
}
